import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productService, recipeService, deductRecipeIngredients } from '../lib/firebaseService';
import { X, Upload, ArrowRight, Check, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { t } from '../utils/translation';
import '../styles/components-extended.css';

export default function ImportWizard({ isOpen, onClose }) {
    const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Review
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [mappings, setMappings] = useState({}); // { 'Raw Name': { type: 'product'|'recipe', id: 'xxx' } }
    const [selectedEventId, setSelectedEventId] = useState('');

    const { data: events } = useQuery({ queryKey: ['events'], queryFn: productService.getEvents });
    const { data: products } = useQuery({ queryKey: ['products'], queryFn: productService.getProducts });
    const { data: recipes } = useQuery({
        queryKey: ['recipes'],
        queryFn: recipeService.getRecipes,
        enabled: step >= 2
    });

    if (!isOpen) return null;

    const handleFileUpload = (e) => {
        const uploadedFile = e.target.files[0];
        if (uploadedFile) {
            setFile(uploadedFile);
            const reader = new FileReader();
            reader.onload = (evt) => {
                const data = evt.target.result;
                const wb = XLSX.read(data, { type: 'array' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];

                // Use header:1 to get array of arrays first to identify headers
                const json = XLSX.utils.sheet_to_json(ws);
                setParsedData(json);
                setStep(2);
            };
            reader.readAsArrayBuffer(uploadedFile);
        }
    };

    const autoMap = () => {
        const newMappings = { ...mappings };
        parsedData.forEach(row => {
            const rawName = row['Description'] || row['Name'] || row['Item'] || 'Unknown';
            if (!newMappings[rawName]) {
                // Try to match with products first
                const productMatch = products?.find(p =>
                    (p.brand + ' ' + p.name).toLowerCase() === rawName.toLowerCase() ||
                    p.name.toLowerCase() === rawName.toLowerCase()
                );

                // Try to match with recipes if no product match
                const recipeMatch = recipes?.find(r =>
                    r.name.toLowerCase() === rawName.toLowerCase()
                );

                if (productMatch) {
                    newMappings[rawName] = { type: 'product', id: productMatch.id };
                } else if (recipeMatch) {
                    newMappings[rawName] = { type: 'recipe', id: recipeMatch.id };
                }
            }
        });
        setMappings(newMappings);
    };

    const getUniqueItems = () => {
        const items = new Set();
        parsedData.forEach(row => {
            const name = row['Description'] || row['Name'] || row['Item'];
            if (name) items.add(name);
        });
        return Array.from(items);
    };

    const uniqueItems = getUniqueItems();
    const unmappedCount = uniqueItems.filter(item => !mappings[item]).length;

    const parseRowTimestamp = (row) => {
        // SumUp Export formats: "Date" (DD/MM/YY) and "Time" (HH:mm)
        // Or sometimes combined. Adjust logic based on actual CSV.
        // Default to current time if missing.
        try {
            const dateStr = row['Date'];
            const timeStr = row['Time'];

            if (dateStr && timeStr) {
                // Parse "15.05.23" or "2023-05-15"
                // This is a naive implementation; library like date-fns or moment would be robust
                // Assuming simple ISO or localized string for now.
                // For this MVP, let's construct a Date object
                // If excel parsed it as a number (serial), this might fail.
                // Let's assume text for now.

                // Combine:
                const [day, month, year] = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('.');
                // Adjust year if 2 digits
                const fullYear = year < 100 ? `20${year}` : year;

                return new Date(`${fullYear}-${month}-${day}T${timeStr}:00`).toISOString();
            }
            return new Date().toISOString();
        } catch (e) {
            console.warn("Date parsing failed", e);
            return new Date().toISOString();
        }
    };

    const handleFinish = async () => {
        try {
            // 1. Create Import Record
            const totalRevenue = parsedData.reduce((acc, row) => {
                const qty = parseInt(row['Quantity'] || row['Qty'] || 1);
                const priceRaw = row['Price (Gross)'] || row['Price'] || row['Amount'] || 0;
                const price = typeof priceRaw === 'string'
                    ? parseFloat(priceRaw.replace(/[^0-9,-]/g, '').replace(',', '.'))
                    : parseFloat(priceRaw);
                const itemRevenue = qty * (isNaN(price) ? 0 : price);
                return acc + itemRevenue;
            }, 0);

            // 1a. Handle "Create New Product" mappings
            const finalMappings = { ...mappings };
            const itemsToCreate = Object.keys(mappings).filter(key => mappings[key] === 'CREATE_NEW');

            if (itemsToCreate.length > 0) {
                await Promise.all(itemsToCreate.map(async (rawName) => {
                    const sampleRow = parsedData.find(r => (r['Description'] || r['Name'] || r['Item']) === rawName);
                    const priceRaw = sampleRow ? (sampleRow['Price (Gross)'] || sampleRow['Price'] || sampleRow['Amount'] || 0) : 0;
                    const price = typeof priceRaw === 'string'
                        ? parseFloat(priceRaw.replace(/[^0-9,-]/g, '').replace(',', '.'))
                        : parseFloat(priceRaw);

                    const newProduct = {
                        brand: 'New',
                        name: rawName,
                        category: 'Uncategorized',
                        price: isNaN(price) ? 0 : price,
                        cost: 0,
                        stock: 0,
                        unitType: 'Unit',
                        lowStockThreshold: 5
                    };

                    const created = await productService.addProduct(newProduct);
                    finalMappings[rawName] = { type: 'product', id: created.id };
                }));
            }

            const importRecord = {
                fileName: file.name,
                date: new Date().toISOString(),
                status: 'completed',
                itemsCount: parsedData.length,
                totalRevenue,
                eventId: selectedEventId || null,
                eventName: events?.find(e => e.id === selectedEventId)?.name || 'Unassigned'
            };

            const savedImport = await productService.addSalesImport(importRecord);

            // 2. Process Line Items - Separate products and recipes
            const productItems = [];
            const recipeItems = [];

            parsedData.forEach(row => {
                const rawName = row['Description'] || row['Name'] || row['Item'] || 'Unknown';
                const mapping = finalMappings[rawName];

                if (!mapping || mapping === 'ignore') return;

                const qty = parseInt(row['Quantity'] || row['Qty'] || 1);
                const priceRaw = row['Price (Gross)'] || row['Price'] || row['Amount'] || 0;
                const price = typeof priceRaw === 'string'
                    ? parseFloat(priceRaw.replace(/[^0-9,-]/g, '').replace(',', '.'))
                    : parseFloat(priceRaw);

                const itemData = {
                    importId: savedImport.id,
                    eventId: selectedEventId || null,
                    productName: rawName,
                    timestamp: parseRowTimestamp(row),
                    quantity: qty,
                    price: isNaN(price) ? 0 : price
                };

                if (mapping.type === 'recipe') {
                    recipeItems.push({
                        ...itemData,
                        recipeId: mapping.id,
                        type: 'recipe'
                    });
                } else {
                    productItems.push({
                        ...itemData,
                        productId: mapping.id,
                        type: 'product'
                    });
                }
            });

            // 3. Save all line items
            await Promise.all([
                ...productItems.map(item => productService.addSalesLineItem(item)),
                ...recipeItems.map(item => productService.addSalesLineItem(item))
            ]);

            // 4. Update Inventory for Products
            const stockDeductions = {};
            productItems.forEach(item => {
                stockDeductions[item.productId] = (stockDeductions[item.productId] || 0) + item.quantity;
            });

            const movementPromises = Object.keys(stockDeductions).map(productId => {
                return productService.addMovement({
                    productId,
                    type: 'out',
                    qty: stockDeductions[productId],
                    reason: 'sales_import',
                    notes: `Import: ${file.name}`,
                    user: 'system',
                    date: new Date().toISOString()
                });
            });

            await Promise.all(movementPromises);

            // 5. Deduct Recipe Ingredients for Recipe-based Sales
            const recipeDeductions = {};
            recipeItems.forEach(item => {
                recipeDeductions[item.recipeId] = (recipeDeductions[item.recipeId] || 0) + item.quantity;
            });

            const recipeDeductionPromises = Object.keys(recipeDeductions).map(async (recipeId) => {
                await deductRecipeIngredients(recipeId, recipeDeductions[recipeId]);
            });

            await Promise.all(recipeDeductionPromises);

            onClose();
        } catch (error) {
            console.error("Import failed", error);
            alert("Import failed. See console.");
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '800px', height: '80vh' }}>
                <div className="modal-header">
                    <h3 className="modal-title">{t('IMPORT_WIZARD.TITLE')}</h3>
                    <button onClick={onClose} className="close-btn"><X size={20} /></button>
                </div>

                <div className="modal-body flex-1 overflow-y-auto">
                    {/* Progress Steps */}
                    <div className="flex justify-between mb-8 px-12">
                        <div className={`flex items-center ${step >= 1 ? 'text-purple-600 font-bold' : 'text-gray-400'}`}>
                            <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 border-current">1</div>
                            {t('IMPORT_WIZARD.STEP_UPLOAD')}
                        </div>
                        <div className={`flex items-center ${step >= 2 ? 'text-purple-600 font-bold' : 'text-gray-400'}`}>
                            <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 border-current">2</div>
                            {t('IMPORT_WIZARD.STEP_MAP')}
                        </div>
                        <div className={`flex items-center ${step >= 3 ? 'text-purple-600 font-bold' : 'text-gray-400'}`}>
                            <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 border-current">3</div>
                            {t('IMPORT_WIZARD.STEP_REVIEW')}
                        </div>
                    </div>

                    {step === 1 && (
                        <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                            <Upload size={48} className="text-gray-400 mb-4" />
                            <p className="text-gray-600 mb-2 font-medium">{t('IMPORT_WIZARD.UPLOAD_TITLE')}</p>
                            <p className="text-xs text-gray-400 mb-4">{t('IMPORT_WIZARD.UPLOAD_DESC')}</p>
                            <input type="file" onChange={handleFileUpload} accept=".csv, .xlsx" className="hidden" id="file-upload" />
                            <label htmlFor="file-upload" className="btn btn-primary">{t('IMPORT_WIZARD.SELECT_FILE')}</label>

                            <div className="mt-8 w-full max-w-xs">
                                <label className="form-label">{t('IMPORT_WIZARD.LINK_EVENT')}</label>
                                <select className="input" value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)}>
                                    <option value="">{t('IMPORT_WIZARD.SELECT_EVENT')}</option>
                                    {events?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <div className="bg-blue-50 p-4 rounded-md mb-4 flex justify-between items-center">
                                <div className="text-blue-800 text-sm">
                                    {t('IMPORT_WIZARD.FOUND_ITEMS')} <strong>{uniqueItems.length}</strong> {t('IMPORT_WIZARD.UNIQUE_ITEMS')}.
                                    {unmappedCount > 0 ? <span className="text-red-600 font-bold ml-1">{unmappedCount} {t('IMPORT_WIZARD.UNMAPPED')}</span> : <span className="text-green-600 font-bold ml-1">{t('IMPORT_WIZARD.ALL_MAPPED')}</span>}
                                </div>
                                <button onClick={autoMap} className="text-blue-600 text-xs font-bold hover:underline">{t('IMPORT_WIZARD.AUTO_MATCH')}</button>
                            </div>

                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="data-table">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th>{t('IMPORT_WIZARD.IMPORTED_NAME')}</th>
                                            <th>{t('IMPORT_WIZARD.MAPS_TO')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {uniqueItems.map(item => (
                                            <tr key={item}>
                                                <td className="font-medium">{item}</td>
                                                <td>
                                                    <select
                                                        className={`input text-sm py-1 ${!mappings[item] ? 'border-red-300 bg-red-50' : ''}`}
                                                        value={typeof mappings[item] === 'object' ? `${mappings[item].type}:${mappings[item].id}` : mappings[item] || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value === '' || value === 'CREATE_NEW' || value === 'ignore') {
                                                                setMappings({ ...mappings, [item]: value });
                                                            } else {
                                                                const [type, id] = value.split(':');
                                                                setMappings({ ...mappings, [item]: { type, id } });
                                                            }
                                                        }}
                                                    >
                                                        <option value="">{t('IMPORT_WIZARD.SELECT_ITEM')}</option>
                                                        <option value="CREATE_NEW" className="font-bold text-green-600">{t('IMPORT_WIZARD.CREATE_NEW')}</option>
                                                        <option value="ignore">{t('IMPORT_WIZARD.IGNORE')}</option>

                                                        <optgroup label="📦 Products">
                                                            {products?.map(p => (
                                                                <option key={p.id} value={`product:${p.id}`}>{p.brand} {p.name}</option>
                                                            ))}
                                                        </optgroup>

                                                        <optgroup label="🍹 Recettes">
                                                            {recipes?.map(r => (
                                                                <option key={r.id} value={`recipe:${r.id}`}>{r.name}</option>
                                                            ))}
                                                        </optgroup>
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="bg-green-50 p-6 rounded-lg text-center">
                                <Check className="mx-auto h-12 w-12 text-green-500 mb-2" />
                                <h3 className="text-lg font-bold text-green-800">{t('IMPORT_WIZARD.READY_TITLE')}</h3>
                                <p className="text-green-700">{t('IMPORT_WIZARD.READY_DESC')}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-center">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="text-2xl font-bold">{parsedData.length}</div>
                                    <div className="text-xs text-gray-500 uppercase">{t('IMPORT_WIZARD.TOTAL_TRANSACTIONS')}</div>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="text-2xl font-bold">{Object.keys(mappings).filter(k => mappings[k] && mappings[k] !== 'ignore').length}</div>
                                    <div className="text-xs text-gray-500 uppercase">{t('IMPORT_WIZARD.MAPPED_PRODUCTS')}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    {step > 1 && <button onClick={() => setStep(step - 1)} className="btn btn-secondary">{t('IMPORT_WIZARD.BTN_BACK')}</button>}
                    {step < 3 && <button onClick={() => setStep(step + 1)} disabled={step === 1 && !file} className="btn btn-primary">{t('IMPORT_WIZARD.BTN_NEXT')} <ArrowRight size={16} className="ml-2" /></button>}
                    {step === 3 && <button onClick={handleFinish} className="btn btn-primary">{t('IMPORT_WIZARD.BTN_CONFIRM')}</button>}
                </div>
            </div>
        </div>
    );
}
